import type { FC } from 'react'
import React, { useRef, useCallback, useMemo } from 'react'
import _range from 'lodash/range'
import _uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import { FormBlockWrapper, SelectInputWrapper, RequiredText, ButtonWrapper } from './style'
import { TimePickerWrapper } from 'components/TimePickerDiffTime/style'
import TimePickerDiffTime from 'components/TimePickerDiffTime'
import SelectOptions, { DEFAULT_OTHER_OPTION_SELECT } from 'components/SelectOptions'
import theme from 'vars/theme'
import { formatTimeUnit, roundUpDateMoment } from 'utils/date'
import { DATETIME_FORMAT } from 'constants/date'

type EntryInstructionTimeReceptionFormProps = {
  entryInstructionDate: string
  entryInstructionHours: string
  entryInstructionMinutes: string
  generalBookingNo?: string
  handleChange: (value: string, name: 'date' | 'hours' | 'minutes') => void
  isEntryInstructionTimeRequired: boolean
  handleClearEntryInstructionTime: () => void
  errors: {
    date: boolean
    hours: boolean
    minutes: boolean
    message: string
  }
}

const EntryInstructionTimeReceptionForm: FC<EntryInstructionTimeReceptionFormProps> = ({
  entryInstructionDate,
  entryInstructionHours,
  entryInstructionMinutes,
  generalBookingNo,
  isEntryInstructionTimeRequired,
  errors,
  handleClearEntryInstructionTime,
  handleChange,
}) => {
  const driverDateRef = useRef(entryInstructionDate || DEFAULT_OTHER_OPTION_SELECT)

  const findAndScrollToElement = useCallback(
    (format: 'HH' | 'mm' | typeof DATETIME_FORMAT.standardDayFormat) => {
      const roundedDate = roundUpDateMoment()
      const value = roundedDate.format(format)

      setTimeout(() => {
        const today =
          entryInstructionDate && entryInstructionHours && entryInstructionMinutes
            ? `${entryInstructionDate} ${entryInstructionHours}:${entryInstructionMinutes}`
            : format === DATETIME_FORMAT.standardDayFormat
              ? roundedDate.format(DATETIME_FORMAT.standardDayFormat)
              : formatTimeUnit(value, format)

        // ページのスクロール位置を記録
        const currentScrollY = window.scrollY

        const matchingItem = document.querySelectorAll('.mono-select__option')
        const el = Array.from(matchingItem).find((el) => el.textContent === today)

        if (el) {
          // メニューリスト内でのスクロールのみを実行
          const menuList = el.closest('.mono-select__menu-list') as HTMLElement
          if (menuList) {
            const elementRect = el.getBoundingClientRect()
            const menuRect = menuList.getBoundingClientRect()
            const relativeTop = elementRect.top - menuRect.top + menuList.scrollTop
            const menuCenter = menuList.clientHeight / 2
            const elementCenter = el.clientHeight / 2

            menuList.scrollTo({
              top: relativeTop - menuCenter + elementCenter,
              behavior: 'auto'
            })

            // ページのスクロール位置を元に戻す
            window.scrollTo(0, currentScrollY)
          }
        }
      }, 0)
    },
    [entryInstructionDate, entryInstructionHours, entryInstructionMinutes],
  )

  const formConfig = useMemo(() => {
    const defaultUnassignOptions = {
      value: DEFAULT_OTHER_OPTION_SELECT,
      label: '未定',
    }

    // Generate date options with the current date and the last 1 days
    const today = moment()
    const dateDefaultSelectOptions = Array.from({ length: 3 }, (_, i) => {
      const targetDate = today.clone().subtract(1 - i, 'days')
      const formattedDate = targetDate.format('YYYY-MM-DD')
      return {
        value: formattedDate,
        label: formattedDate,
      }
    })

    const formattedDates = generalBookingNo
      ? [
        ...dateDefaultSelectOptions,
        {
          label: driverDateRef.current,
          value: driverDateRef.current,
        },
      ]
      : dateDefaultSelectOptions
    const orderedOption = _uniqBy(formattedDates, 'value').sort((a, b) =>
      moment(a.value).diff(moment(b.value)),
    )

    const newDataDates = [defaultUnassignOptions, ...orderedOption].filter(
      (item) => item.label !== DEFAULT_OTHER_OPTION_SELECT,
    )

    // Return form configuration
    return {
      formWidth: 360,
      options: {
        dates: newDataDates,
        hours: [
          defaultUnassignOptions,
          ..._range(0, 24).map((hour) => ({
            label: `${hour.toString().padStart(2, '0')}時`,
            value: hour.toString().padStart(2, '0'),
          })),
        ],
        minutes: [
          defaultUnassignOptions,
          ..._range(0, 60, 5).map((minute) => ({
            label: `${minute.toString().padStart(2, '0')}分`,
            value: minute.toString().padStart(2, '0'),
          })),
        ],
      },
    }
  }, [generalBookingNo])

  return (
    <FormBlockWrapper>
      <div
        className="d-flex mb-3 flex-shrink-0 align-items-center"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: isEntryInstructionTimeRequired ? '490px' : '430px',
        }}
      >
        <h1 className="text-primary">入場指示時刻</h1>
        <div>
          <ButtonWrapper
            outline
            color="primary"
            className="bg-white ml-2 px-md-4 px-sm-1 px-0 text-primary"
            size="sm"
            height="40px"
            style={{ whiteSpace: 'nowrap' }}
            onClick={handleClearEntryInstructionTime}
          >
            クリア
          </ButtonWrapper>
        </div>
      </div>

      <div className="d-flex">
        <SelectInputWrapper>
          {isEntryInstructionTimeRequired && <RequiredText>必須</RequiredText>}
          <div style={{ width: 95 }} className="label">
            指示日時
          </div>
          <div className="d-flex">
            <div style={{ width: 140 }}>
              <SelectOptions
                className="w-100 bg-white"
                name="entryInstructionDate"
                options={formConfig.options.dates}
                selectValue={entryInstructionDate || DEFAULT_OTHER_OPTION_SELECT}
                handleUpdateData={(value: string) => handleChange(value, 'date')}
                skipAutoUpdate
                isError={errors.date}
                styles={{
                  control: (provided: any) => ({
                    ...provided,
                    height: '60px',
                    minHeight: '60px',
                  }),
                  option: (provided: any, { label }: { label: string }) => {
                    const isDefaultDate =
                      entryInstructionDate === DEFAULT_OTHER_OPTION_SELECT || !entryInstructionDate
                    return isDefaultDate &&
                      label === roundUpDateMoment().format(DATETIME_FORMAT.standardDayFormat)
                      ? {
                        ...provided,
                        color: 'black',
                        fontWeight: 'bold',
                      }
                      : provided
                  },
                }}
                onMenuOpen={() => findAndScrollToElement(DATETIME_FORMAT.standardDayFormat)}
              />
            </div>
            <div style={{ width: 190, marginLeft: '4px' }}>
              <div className="d-flex">
                <TimePickerWrapper>
                  <TimePickerDiffTime
                    name="entryInstructionHours"
                    options={formConfig.options.hours}
                    selectValue={entryInstructionHours}
                    onChange={(value) => handleChange(value, 'hours')}
                    hasClass={false}
                    isError={errors.hours}
                    displayValue={
                      entryInstructionHours === DEFAULT_OTHER_OPTION_SELECT || !entryInstructionHours
                        ? formatTimeUnit(roundUpDateMoment().format('HH'), 'HH')
                        : formatTimeUnit(entryInstructionHours, 'HH')
                    }
                    onMenuOpen={() => findAndScrollToElement('HH')}
                  />
                </TimePickerWrapper>
                <TimePickerWrapper>
                  <TimePickerDiffTime
                    name="entryInstructionMinutes"
                    options={formConfig.options.minutes}
                    selectValue={entryInstructionMinutes}
                    onChange={(value) => handleChange(value, 'minutes')}
                    hasClass={false}
                    isError={errors.minutes}
                    displayValue={
                      entryInstructionMinutes === DEFAULT_OTHER_OPTION_SELECT ||
                        !entryInstructionMinutes
                        ? formatTimeUnit(roundUpDateMoment().format('mm'), 'mm')
                        : formatTimeUnit(entryInstructionMinutes, 'mm')
                    }
                    onMenuOpen={() => findAndScrollToElement('mm')}
                  />
                </TimePickerWrapper>
              </div>
            </div>
          </div>
          <div style={{ marginLeft: '12px' }}>
            <div className="flex-1" style={{ whiteSpace: 'nowrap' }}>
              <p className="font-md mb-0">入場時刻を指定されている場合は入力をしてください。</p>
              {!isEntryInstructionTimeRequired && (
                <p className="font-md mb-0">
                  特に指定も予約もされていない場合は未定のままでも問題ありません。
                </p>
              )}
            </div>
          </div>
        </SelectInputWrapper>
      </div>

      <div className="d-flex">
        <div style={{ width: 95 }} className="label" />
        <div className="d-flex align-items-center mb-1 mt-1">
          {errors && (
            <p
              className="text-left ml-1"
              style={{ color: `${theme.redMedium1}`, fontSize: '12px' }}
            >
              {errors.message}
            </p>
          )}
        </div>
      </div>
    </FormBlockWrapper>
  )
}

export default EntryInstructionTimeReceptionForm
